import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ListChecks, Play, RefreshCw } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => {
  const t = localStorage.getItem("apex_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};
const TONE = {
  BUY: "text-emerald-300 bg-emerald-500/15 border-emerald-500/40",
  WAIT: "text-amber-300 bg-amber-500/15 border-amber-500/40",
  SKIP: "text-rose-300 bg-rose-500/15 border-rose-500/40",
  ERROR: "text-slate-400 bg-slate-700/40 border-slate-600",
};
const LABEL = { BUY: "ACHETER", WAIT: "ATTENDRE", SKIP: "PASSER", ERROR: "ERREUR" };
const SLOT = { morning: "8h30", afternoon: "16h00", manual: "manuel" };
const fmt = (iso) => new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

const Row = ({ r }) => (
  <div className="flex items-center gap-2 text-xs py-1" data-testid={`desk-wl-row-${r.ticker}`}>
    <span className="font-bold text-slate-100 w-20 shrink-0">{r.ticker}</span>
    {r.mom_5d != null && (
      <span className="text-[10px] text-slate-500 w-28 shrink-0" data-testid={`desk-wl-mv-${r.ticker}`}>
        5j {r.mom_5d > 0 ? "+" : ""}{r.mom_5d}% · vol ×{r.vol_ratio}
      </span>
    )}
    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border shrink-0 ${TONE[r.verdict] || TONE.ERROR}`} data-testid={`desk-wl-verdict-${r.ticker}`}>
      {LABEL[r.verdict] || r.verdict} · {r.confidence}%
    </span>
    <span className="text-slate-400 truncate">{r.reason}</span>
  </div>
);

export const DeskWatchlist = ({ compact = false }) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(() => axios.get(`${API}/api/sniper/desk/watchlist`, { headers: authHeaders() })
    .then((r) => setData(r.data)).catch(() => {}), []);

  useEffect(() => {
    load();
    const iv = setInterval(load, data?.running ? 10000 : 60000);
    return () => clearInterval(iv);
  }, [load, data?.running]);

  const run = async () => {
    setErr("");
    try {
      await axios.post(`${API}/api/sniper/desk/watchlist/run`, {}, { headers: authHeaders() });
      setData((d) => ({ ...(d || {}), running: true }));
      setTimeout(load, 3000);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Impossible de lancer le desk");
    }
  };

  const runDoc = data?.run;
  const buys = data?.today_buys || [];
  const results = runDoc?.results || [];

  if (compact) {
    if (!runDoc) return null;
    return (
      <div className="mb-3 rounded-lg border border-cyan-700/30 bg-cyan-950/20 px-3 py-2" data-testid="brief-desk-watchlist">
        <div className="flex items-center gap-1.5 text-[11px] font-bold text-cyan-300 mb-1">
          <ListChecks className="w-3.5 h-3.5" /> Desk watchlist · {SLOT[runDoc.slot] || runDoc.slot} · {fmt(runDoc.at)}
        </div>
        {buys.length
          ? buys.map((r) => <Row key={r.ticker} r={r} />)
          : <div className="text-[11px] text-slate-400" data-testid="brief-desk-no-buy">Aucun « ACHETER » aujourd'hui sur le top 5 de la watchlist.</div>}
      </div>
    );
  }

  return (
    <div className="mb-5 rounded-xl border border-slate-700/60 bg-slate-900/40 p-4" data-testid="desk-watchlist">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <ListChecks className="w-4 h-4 text-cyan-400" />
        <span className="font-semibold text-slate-100 text-sm">Top 5 watchlist</span>
        <span className="text-[11px] text-slate-500">auto à 8h30 et 16h00 · les « ACHETER » passent en priorité au Sniper</span>
        <button onClick={run} disabled={data?.running}
          className="ml-auto flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 bg-cyan-600 hover:bg-cyan-500 disabled:bg-slate-700 text-white transition-colors"
          data-testid="desk-wl-run-btn">
          {data?.running ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
          {data?.running ? "Analyse en cours (~2 min)…" : "Lancer sur ma watchlist"}
        </button>
      </div>
      {err && <div className="text-xs text-rose-300 mb-2" data-testid="desk-wl-error">{err}</div>}
      {runDoc && <div className="text-[11px] text-slate-500 mb-1" data-testid="desk-wl-meta">Passage {SLOT[runDoc.slot] || runDoc.slot} · {fmt(runDoc.at)} · {buys.length} ACHETER aujourd'hui</div>}
      {results.length
        ? <div className="divide-y divide-slate-800">{results.map((r) => <Row key={r.ticker} r={r} />)}</div>
        : <div className="text-xs text-slate-500" data-testid="desk-wl-empty">{data?.running ? "Le desk débat sur la première action…" : "Pas encore de passage sur la watchlist."}</div>}
    </div>
  );
};
